// frontend/src/components/librarian/dashboard/Panel.tsx
import React from 'react';
import { Link } from 'react-router-dom';

interface Props {
  title: string;
  blurb?: string;
  /** A "View all" link, or any control (e.g. a range toggle). */
  action?: { label: string; to: string } | React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const isLink = (a: Props['action']): a is { label: string; to: string } =>
  !!a && typeof a === 'object' && 'to' in a && 'label' in a;

/** A dashboard panel: heading row, then whatever the section holds. */
const Panel: React.FC<Props> = ({ title, blurb, action, className = '', children }) => (
  <section
    aria-label={title}
    className={`flex flex-col rounded-lg border border-[var(--lib-hairline)] bg-[var(--lib-surface)] ${className}`}
  >
    <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4">
      <div className="min-w-0">
        <h2 className="font-display text-[16px] font-semibold text-[var(--lib-ink)]">{title}</h2>
        {blurb && <p className="mt-0.5 text-[12px] text-[var(--lib-ink-muted)]">{blurb}</p>}
      </div>
      {isLink(action) ? (
        <Link to={action.to} className="shrink-0 text-[13px] text-[var(--lib-gold-text)] underline-offset-4 hover:underline">
          {action.label}
        </Link>
      ) : (
        action
      )}
    </div>
    {children}
  </section>
);

/** Placeholder rows while a panel loads. */
export const PanelSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div aria-hidden className="px-5 pb-5 space-y-3">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="h-3 rounded bg-[var(--lib-surface-2)] animate-pulse" style={{ width: `${88 - i * 9}%` }} />
    ))}
  </div>
);

export default Panel;
